import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Wrench, 
  Plus, 
  MoreVertical, 
  Trash2, 
  Search,
  Filter,
  Calendar,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const serviceTypes = [
  "Oil Change",
  "Tire Replacement",
  "Brake Service",
  "Engine Tune-up",
  "Full Service",
  "Battery Replacement",
  "Transmission Service",
  "Air Filter",
  "Coolant Flush",
  "Other"
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function ServiceHistoryPage() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    vehicle_id: "",
    service_type: "Oil Change",
    description: "",
    cost: 0,
    service_date: new Date().toISOString().split("T")[0],
    next_service_date: "",
    mileage_at_service: 0,
    organization_id: ""
  });

  const canEdit = user?.role === "admin" || user?.role === "fleet_manager";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, vehiclesRes, orgsRes] = await Promise.all([
        apiClient.get("/service-records"),
        apiClient.get("/vehicles"),
        apiClient.get("/organizations")
      ]);
      setRecords(recordsRes.data);
      setVehicles(vehiclesRes.data);
      setOrganizations(orgsRes.data);
      
      if (vehiclesRes.data.length > 0 && !formData.vehicle_id) {
        setFormData(prev => ({ 
          ...prev, 
          vehicle_id: vehiclesRes.data[0].id,
          organization_id: vehiclesRes.data[0].organization_id,
          mileage_at_service: vehiclesRes.data[0].mileage
        }));
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/service-records", formData);
      toast.success("Service record added successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (recordId) => {
    if (!confirm("Are you sure you want to delete this record?")) return;
    try {
      await apiClient.delete(`/service-records/${recordId}`);
      toast.success("Service record deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete record");
    }
  };

  const resetForm = () => {
    const defaultVehicle = vehicles[0];
    setFormData({
      vehicle_id: defaultVehicle?.id || "",
      service_type: "Oil Change",
      description: "",
      cost: 0,
      service_date: new Date().toISOString().split("T")[0],
      next_service_date: "",
      mileage_at_service: defaultVehicle?.mileage || 0,
      organization_id: defaultVehicle?.organization_id || ""
    });
  };

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setFormData({ 
      ...formData, 
      vehicle_id: vehicleId,
      organization_id: vehicle?.organization_id || "",
      mileage_at_service: vehicle?.mileage || 0
    });
  };

  const filteredRecords = records.filter(record => {
    const vehicle = vehicles.find(v => v.id === record.vehicle_id);
    const vehicleInfo = vehicle ? `${vehicle.registration_number} ${vehicle.make} ${vehicle.model}` : "";
    const matchesSearch = 
      vehicleInfo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.service_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVehicle = vehicleFilter === "all" || record.vehicle_id === vehicleFilter;
    const matchesType = typeFilter === "all" || record.service_type === typeFilter;
    return matchesSearch && matchesVehicle && matchesType;
  });

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.registration_number} - ${vehicle.make} ${vehicle.model}` : "Unknown";
  };

  const totalCost = filteredRecords.reduce((sum, r) => sum + r.cost, 0);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="service-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="skeleton-tactical h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            SERVICE HISTORY
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Track maintenance and repair records
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="add-service-btn">
                <Plus className="w-4 h-4 mr-2" />
                ADD RECORD
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border rounded-sm max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg tracking-wide">
                  ADD SERVICE RECORD
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="service-form">
                <div className="space-y-2">
                  <Label className="text-xs font-mono text-muted-foreground uppercase">Vehicle</Label>
                  <Select value={formData.vehicle_id} onValueChange={handleVehicleChange}>
                    <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="service-vehicle-select">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border rounded-sm">
                      {vehicles.map(vehicle => (
                        <SelectItem key={vehicle.id} value={vehicle.id} className="font-mono text-sm">
                          {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-mono text-muted-foreground uppercase">Service Type</Label>
                  <Select value={formData.service_type} onValueChange={(value) => setFormData({ ...formData, service_type: value })}>
                    <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="service-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border rounded-sm">
                      {serviceTypes.map(type => (
                        <SelectItem key={type} value={type} className="font-mono text-sm">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-mono text-muted-foreground uppercase">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the service performed..."
                    className="bg-input border-border rounded-sm font-mono text-sm min-h-[80px]"
                    required
                    data-testid="service-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Cost (TZS)</Label>
                    <Input
                      type="number"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="service-cost-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Mileage (km)</Label>
                    <Input
                      type="number"
                      value={formData.mileage_at_service}
                      onChange={(e) => setFormData({ ...formData, mileage_at_service: parseInt(e.target.value) })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="service-mileage-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Service Date</Label>
                    <Input
                      type="date"
                      value={formData.service_date}
                      onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="service-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Next Service</Label>
                    <Input
                      type="date"
                      value={formData.next_service_date}
                      onChange={(e) => setFormData({ ...formData, next_service_date: e.target.value })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      data-testid="service-next-date-input"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="service-submit-btn">
                  ADD RECORD
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground">{records.length}</p>
              <p className="text-xs font-mono text-muted-foreground">TOTAL RECORDS</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-xl font-heading font-bold text-foreground">{formatCurrency(totalCost)}</p>
              <p className="text-xs font-mono text-muted-foreground">TOTAL COST</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold text-foreground">
                {records.filter(r => r.next_service_date && new Date(r.next_service_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)).length}
              </p>
              <p className="text-xs font-mono text-muted-foreground">DUE THIS MONTH</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-input border-border rounded-sm font-mono text-sm"
                data-testid="service-search-input"
              />
            </div>
            <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-border rounded-sm font-mono text-sm" data-testid="service-vehicle-filter">
                <SelectValue placeholder="Filter by vehicle" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="all" className="font-mono text-sm">All Vehicles</SelectItem>
                {vehicles.map(vehicle => (
                  <SelectItem key={vehicle.id} value={vehicle.id} className="font-mono text-sm">
                    {vehicle.registration_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-border rounded-sm font-mono text-sm" data-testid="service-type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="all" className="font-mono text-sm">All Types</SelectItem>
                {serviceTypes.map(type => (
                  <SelectItem key={type} value={type} className="font-mono text-sm">{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card className="bg-card border-border rounded-sm" data-testid="service-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">DATE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">VEHICLE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">SERVICE TYPE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">DESCRIPTION</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">MILEAGE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">COST</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">NEXT SERVICE</TableHead>
                  {canEdit && <TableHead className="font-heading text-xs tracking-wide text-muted-foreground text-right">ACTIONS</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow key={record.id} className="border-border hover:bg-accent/50" data-testid={`service-row-${record.id}`}>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {record.service_date}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-primary">
                      {getVehicleInfo(record.vehicle_id)}
                    </TableCell>
                    <TableCell className="font-medium text-foreground">
                      {record.service_type}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                      {record.description}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {record.mileage_at_service.toLocaleString()} km
                    </TableCell>
                    <TableCell className="font-mono text-sm text-yellow-400">
                      {formatCurrency(record.cost)}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {record.next_service_date || "-"}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`service-actions-${record.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
                            <DropdownMenuItem 
                              onClick={() => handleDelete(record.id)} 
                              className="cursor-pointer text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filteredRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 8 : 7} className="text-center py-8 text-muted-foreground font-mono">
                      No service records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
